import { useQuery } from "@apollo/client";
import { DocumentNode, GraphQLFormattedError } from "graphql";
import { useCallback, useEffect, useState } from "react";
import { useQueryParam, withDefault, StringParam } from "use-query-params";

import { TrackedJobStatus } from "~frontend/__generated__/graphql";
import { Subaccount } from "~frontend/providers/LoggedInUserProvider";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import {
  DEFAULT_TIMEFRAME,
  Timeframe,
  timeframeToStartAndEndDate,
} from "./timeframes";

export function useAnalyticsDashboardData<D>({
  dataQuery,
  dataSelector,
  computeDataMutation,
  subaccount,
}: {
  dataQuery: DocumentNode;
  dataSelector: (data: D) =>
    | {
        computeJob?: { status: TrackedJobStatus } | null;
      }
    | null
    | undefined;
  computeDataMutation: DocumentNode;
  subaccount?: Subaccount;
}): {
  timeframe: Timeframe;
  refresh: (newTimeframe: Timeframe) => Promise<void>;
  loading: boolean;
  computeInProgress: boolean;
  data?: D;
  errors: any[];
} {
  const [timeframeStr, setTimeframe] = useQueryParam(
    "timeframe",
    withDefault(StringParam, DEFAULT_TIMEFRAME)
  );
  const timeframe = timeframeStr as Timeframe;

  const { data, loading, error, startPolling, stopPolling } = useQuery<D>(
    dataQuery,
    {
      variables: {
        input: {
          subaccountId: subaccount?.id,
          ...timeframeToStartAndEndDate(timeframe),
        },
      },
    }
  );

  const [computeData, { loading: computeRequestLoading }] = useMutation(
    computeDataMutation,
    {
      onErrorSnackbarMessage: "Error refreshing dashboard data.",
      refetchQueries: [dataQuery],
      awaitRefetchQueries: true,
    }
  );
  const [computeDataErrors, setComputeDataErrors] = useState<
    readonly GraphQLFormattedError[]
  >([]);
  const handleComputeData = useCallback(
    async (timeframe: Timeframe) => {
      setComputeDataErrors([]);

      const { errors } = await computeData({
        variables: {
          input: {
            subaccountId: subaccount?.id,
            ...timeframeToStartAndEndDate(timeframe),
          },
        },
      });

      if (errors) {
        setComputeDataErrors(errors);
      }
    },
    [setComputeDataErrors, computeData, subaccount]
  );

  useEffect(() => {
    void (async () => {
      if (
        data &&
        !dataSelector(data) &&
        !computeRequestLoading &&
        !computeDataErrors.length
      ) {
        await handleComputeData(timeframe);
      }
    })();
  }, [
    data,
    dataSelector,
    computeRequestLoading,
    computeDataErrors,
    handleComputeData,
    timeframe,
  ]);

  const computeJobInProgress =
    data &&
    dataSelector(data) &&
    (!dataSelector(data)!.computeJob ||
      jobInProgress(dataSelector(data)!.computeJob!));

  useEffect(() => {
    if (computeJobInProgress) {
      startPolling(1000);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [computeJobInProgress, startPolling, stopPolling]);

  return {
    timeframe,
    refresh: async newTimeframe => {
      setTimeframe(newTimeframe);
      await handleComputeData(newTimeframe);
    },
    computeInProgress: computeJobInProgress ?? false,
    loading: loading || computeRequestLoading,
    data,
    errors: [...(error ? [error] : []), ...(computeDataErrors ?? [])],
  };
}
